import React, { ReactElement } from "react";
import {
    useGetAgentsQuery, User, UserGroup,
} from "../../../__generated__/api";
import Avatar from "../../avatar/Avatar";
import Select, { Option, OptionWithChildren, SelectInstanceHandler } from "../../select/Select";
import { Filters } from "../../filters/Filters";

export type AgentOrGroup = {
    type: "user",
    data: User
} | {
    type: "group",
    data: UserGroup
}

export function isAgentOrGroup(item: AgentOrGroup | User | UserGroup): item is AgentOrGroup {
    return Object.hasOwn(item, "type");
}

export function isAgentOrGroupArray(item: AgentOrGroup[] | User[] | UserGroup[]): item is AgentOrGroup[] {
    return Object.hasOwn(item.firstElement, "type");
}

export interface AgentPickerProps<TIsMulti = false, TIsClearable = false, TShouldIncludeGroups = false> {
    isMulti?: TIsMulti;
    minRequired?: number;
    placeholder?: string;
    className?: string;
    shouldIncludeGroups?: TShouldIncludeGroups;
    isClearable?: TIsClearable;
    exclude?: string[];
    toggleText?: string | ReactElement;
    shouldShowDropdown?: boolean;
    searchMinimum?: number;
    toggleClassName?: string;

    value: (TIsMulti extends true ? string[] : string) | undefined;
    onSelected: (
        sourceId: (TIsMulti extends true ? string[] : string) | (TIsClearable extends true ? undefined : never),
        agents: (TShouldIncludeGroups extends true
            ? TIsMulti extends true ? AgentOrGroup[] : AgentOrGroup
            : TIsMulti extends true ? User[] : User)
        | (TIsClearable extends true ? undefined : never)
    ) => void;
}

const AgentPicker = React.forwardRef(
    <TIsMulti extends boolean = false, TIsClearable extends boolean = false, TShouldIncludeGroups extends boolean = false>(
        {
            isMulti, onSelected, placeholder,
            minRequired, value, className, shouldIncludeGroups, isClearable, exclude,
            shouldShowDropdown, searchMinimum, toggleClassName, toggleText,
        }: AgentPickerProps<TIsMulti, TIsClearable, TShouldIncludeGroups>, ref: React.ForwardedRef<SelectInstanceHandler>,
    ): ReactElement => {
        const { data, loading, error } = useGetAgentsQuery({
            variables: {
                shouldIncludeGroups: shouldIncludeGroups as boolean,
            },
        });

    type SelectedType = TIsMulti extends true ? readonly Option<AgentOrGroup>[] : Option<AgentOrGroup>;
    type ValueType = TIsMulti extends true ? Option<AgentOrGroup>[] : Option<AgentOrGroup>;

    const handleSelected = (selected: SelectedType) => {
        if (!selected) {
            (onSelected as (empty: unknown) => void)(undefined);
            return;
        }
        const values = (Array.isArray(selected)
            ? selected.map(row => row.value as string)
            : (selected as Option<AgentOrGroup>).value) as Parameters<typeof onSelected>[0];
        const agents = (Array.isArray(selected)
            ? selected.map(row => (row as Option<AgentOrGroup>).data)
            : (selected as Option<AgentOrGroup>).data) as Parameters<typeof onSelected>[1];
        onSelected(values, agents);
    };

    const excludeSet = new Set(exclude ?? []);
    const agentValues: Option<Partial<AgentOrGroup>>[] = data?.settings?.agents?.filter(row => !excludeSet.has(row.id))
        .map(agent => ({
            label: (
                <>
                    <Avatar src={agent.avatarUrl as string} variant="xs" />
                    {agent.name}
                </>
            ),
            value: agent.id.toString() as string,
            text: agent.name,
            data: {
                type: "user",
                data: agent as User,
            },
        })) ?? [];

    const groupValues: Option<Partial<AgentOrGroup>>[] = shouldIncludeGroups
        ? data?.userGroups?.filter(row => !excludeSet.has(row.id)).map(group => ({
            label: group.name as string,
            value: group.id.toString() as string,
            data: {
                type: "group",
                data: group as UserGroup,
            },
        })) ?? []
        : [];

    let options: (Option<Partial<AgentOrGroup>> | OptionWithChildren<Partial<AgentOrGroup>>)[];

    options = agentValues;

    if (shouldIncludeGroups) {
        options = [
            {
                label: "Groups",
                options: groupValues,
            },
            {
                label: "Agents",
                options: agentValues,
            },
        ];
    }

    const values = [...agentValues, ...groupValues];
    const valueMap = values.toIdMap("value");
    const selected = Array.isArray(value) ? value.map(row => valueMap[row]) : valueMap[value as string];

    if (shouldShowDropdown) {
        return (
            <Filters
                toggleText={toggleText}
                isMulti={isMulti}
                searchMinimum={searchMinimum}
                categories={options}
                className={className}
                toggleClassName={toggleClassName}
                selectedCategories={
                    (isMulti
                        ? new Set(!value || Array.isArray(value) ? value : [value])
                        : (value as string)) as TIsMulti extends true ? Set<string> : string
                }
                onSelected={current => {
                    if (isMulti) {
                        const handler = (onSelected as ((value: string[], users: (AgentOrGroup | User)[]) => void));
                        const list = Array.from(current);
                        handler(list, list.map(id => (shouldIncludeGroups
                            ? valueMap[id].data as AgentOrGroup
                            : valueMap[id].data?.data as User)));
                    } else {
                        const handler = (onSelected as ((value: string, user: AgentOrGroup | User) => void));
                        handler(current as string, shouldIncludeGroups
                            ? valueMap[current as string].data as AgentOrGroup
                            : valueMap[current as string].data?.data as User);
                    }
                }}
            />
        );
    }
    return (
        <Select
            ref={ref}
            filterOption={(member, input) => {
                if (input) {
                    const lower = input.toLowerCase();
                    return (member.data as UserGroup).name?.toLowerCase().includes(lower)
                     || (member.data as User).name?.toLowerCase().includes(lower);
                }

                return true;
            }}
            minRequired={minRequired}
            placeholder={placeholder}
            isMulti={isMulti}
            onChange={handleSelected}
            options={(options ?? []) as Option<AgentOrGroup>[]}
            value={selected as ValueType}
            className={className}
            isClearable={isClearable}
        />
    );
    },
);

AgentPicker.defaultProps = {
    isMulti: false,
    minRequired: undefined,
    placeholder: undefined,
    className: undefined,
    shouldIncludeGroups: false,
    isClearable: undefined,
    exclude: [],
    toggleText: "",
    searchMinimum: undefined,
    shouldShowDropdown: false,
    toggleClassName: "",
};

export default AgentPicker as <
    TMulti extends boolean = false,
    TIsClearable extends boolean = false,
    TShouldIncludeGroups extends boolean = false,
> (
    props: AgentPickerProps<TMulti, TIsClearable, TShouldIncludeGroups> & { ref?: React.ForwardedRef<SelectInstanceHandler>; }
) => ReturnType<typeof AgentPicker>;
